// @flow
import React, { Fragment, Component } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import withStyles from "elevate-ui/withStyles";
import Input from "elevate-ui/Input";
import RadioGroup from "elevate-ui/RadioGroup";
import Typography from "elevate-ui/Typography";
import classNames from "classnames";
import Button from "elevate-ui/Button";
import Captcha from "../common/Captcha";
import OAuthGoogle from "../common/OAuthGoogle";
import OAuthFacebook from "../common/OAuthFacebook";
import { withGlobalContext } from "../common/GlobalContext";
import PhoneInput from "../common/PhoneInput";

type $Props = {
  classes: Object,
  className: string,
  tenant: string,
  location: Object,
  assertCaptcha: Function,
  assertCaptchaV2: Function,
  isPageForceV2Captcha: Function,
};

type $State = {
  token: string,
};

class SignupForm extends Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);
    this.state = {
      token: "",
    };
  }

  componentDidMount = () => {
    const { assertCaptchaV2, isPageForceV2Captcha } = this.props;
    if (isPageForceV2Captcha()) {
      assertCaptchaV2();
    }
  };

  onSubmit = () => {
    const { assertCaptcha, isPageForceV2Captcha } = this.props;
    if (isPageForceV2Captcha()) {
      // $FlowIgnore
      document.getElementById("signup-form").submit();
    } else {
      assertCaptcha("signup").then((token) => {
        this.setState({ token }, () => {
          // $FlowIgnore
          document.getElementById("signup-form").submit();
        });
      });
    }
  };

  render() {
    const {
      classes,
      className,
      tenant,
      location,
      isPageForceV2Captcha,
    } = this.props;
    return (
      <div className={classNames(classes.root, className)}>
        <Typography type="heading2" gutterBottom className={classes.heading}>
          Create Account
        </Typography>
        <Typography type="body" className={classes.subHeading}>
          Create an account to manage your user profile, favorited listings,
          saved searches and more.
        </Typography>
        <Formik
          initialValues={{
            firstName: "",
            lastName: "",
            email: "",
            password: "",
            phone: "",
            preferredContactMethod: "email",
          }}
          validationSchema={() =>
            Yup.object().shape({
              firstName: Yup.string().required("First name is required"),
              lastName: Yup.string().required("Last name is required"),
              email: Yup.string()
                .email("Invalid email address")
                .required("Email is required"),
              password: Yup.string()
                .min(8, "Password must be at least 8 characters long")
                .required("Password is required"),
              phone: Yup.string().matches(
                /^\([0-9]{3}\) [0-9]{3}-[0-9]{4}$/,
                "Please enter valid phone number"
              ),
              preferredContactMethod: Yup.string(),
            })
          }
          onSubmit={this.onSubmit}
          render={({ isSubmitting, values }) => (
            <Form method="POST" id="signup-form" noValidate>
              <div className={classes.row}>
                <Field
                  id="firstName"
                  name="firstName"
                  label="First Name"
                  component={Input}
                />
                <Field
                  id="lastName"
                  name="lastName"
                  label="Last Name"
                  component={Input}
                />
              </div>
              <div className={classes.row}>
                <Field
                  id="email"
                  name="email"
                  label="Email"
                  component={Input}
                />
              </div>
              <div className={classes.row}>
                <Field
                  id="phone"
                  name="phone"
                  label="Phone"
                  component={PhoneInput}
                />
              </div>
              {values.phone.length > 0 && (
                <div>
                  <Field
                    id="preferredContactMethod"
                    name="preferredContactMethod"
                    label="I prefer to be contacted via:"
                    component={RadioGroup}
                    items={[
                      { label: "Call", value: "call" },
                      { label: "Text", value: "text" },
                      { label: "Email", value: "email" },
                    ]}
                    display="inline"
                  />
                </div>
              )}
              <div className={classes.row}>
                <Field
                  type="password"
                  id="password"
                  name="password"
                  label="Create A Password"
                  component={Input}
                />
              </div>
              <Captcha
                token={this.state.token}
                isPageForceV2Captcha={isPageForceV2Captcha()}
              />
              <Button
                type="submit"
                className={classNames(classes.signupBtn)}
                disabled={isSubmitting}
              >
                <span className={classes.signupText}>Sign up</span>
              </Button>
            </Form>
          )}
        />
        {tenant && tenant === "elevate" ? (
          <Fragment>
            <div className={classes.or}>
              <span className={classes.orSpan}> or </span>
            </div>
            <div
              className={classNames({
                [classes.socialSignup]: true,
                ref__sso: tenant && tenant === "elevate",
              })}
            >
              <OAuthGoogle href="/auth/google" title="Sign up with Google" />
              <OAuthFacebook
                href="/auth/facebook"
                title="Sign up with Facebook"
              />
            </div>
          </Fragment>
        ) : null}
        <Typography type="body" className={classes.signupDisclaimer}>
          By clicking “Sign Up”, I provide my signature expressly consenting
          that real estate professionals and Elm Street Technology may contact
          me at the number provided via text, email or call (may involve
          automated or pre-recorded means). I am not required to enter into this
          agreement as a condition of any purchase. I can revoke this consent
          through any reasonable means.
        </Typography>
        <div className={classes.closingContainer}>
          Already have an account?{" "}
          <span>
            <a className={classes.signup} href={`/login${location.search}`}>
              Log in here.
            </a>
          </span>
        </div>
      </div>
    );
  }
}

export default withStyles((theme) => ({
  "@global": {
    body: {
      backgroundColor: "#fff !important",
    },
  },
  root: {
    display: "flex",
    flexDirection: "column",
    maxWidth: "400px",
    padding: "0px 12px",
    margin: "96px auto",
  },
  heading: {
    textAlign: "center",
  },
  subHeading: {
    textAlign: "center",
    margin: "auto",
    marginBottom: theme.spacing.unit * 3,
  },
  signupDisclaimer: {
    textAlign: "center",
    margin: "auto",
    fontWeight: "600",
    fontSize: "14px",
    marginTop: theme.spacing.unit,
  },
  row: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",

    "& > * + *": {
      marginLeft: theme.spacing.unit * 0.75,
    },
  },
  seperator: {
    display: "flex",
    flexDirection: "row",
    margin: "20px auto",
  },
  or: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    fontWeight: "600",
    fontSize: "14px",
    color: theme.colors.gray["600"],
    marginBottom: theme.spacing.unit,

    "&:after, &:before": {
      content: "''",
      display: "block",
      background: theme.colors.gray["300"],
      width: "200px",
      height: "2px",
      margin: "20px 0",
    },
  },
  orSpan: {
    padding: "0 8px",
  },
  socialSignup: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",

    "& > * + *": {
      marginLeft: theme.spacing.unit * 0.75,
    },
  },
  closingContainer: {
    marginTop: theme.spacing.unit * 4,
    color: theme.colors.gray["500"],
    fontWeight: "600",
    fontSize: "18px",
    maxWidth: "330px",
    margin: "0 auto",
  },
  signupBtn: {
    width: "100%",
    height: "54px",
    margin: "16px 0",
    fontSize: "24px",
    fontWeight: "600",
  },
  signup: {
    color: `${theme.colors.gray5002E2E35} !important`,
    textDecoration: "underline",

    "&:hover": {
      textDecoration: "none !important",
    },
  },
  signupText: {
    fontSize: "20px",
  },
}))(withGlobalContext(SignupForm));
